import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';

import HabitatLink from '@/components/HabitatLink';
import { getHabitatQueryOptions } from '@/queries/habitats';
import { Video, VideoV2 } from '@/queries/media/types';
import { getPlaylistQueryOption } from '@/queries/playlists';
import { trackMP } from '@/shared/mp';

import FollowButton from './FollowButton';
import HabitatProfileImageLink from './HabitatProfileImageLink';

interface HomeHighlightVideoWithPlaylistInfoBarProps {
  video: Video | VideoV2;
  playlistId: string;
  onNextVideo?: () => void;
  onBackToAllPlaylists?: () => void;
}

const HomeHighlightVideoWithPlaylistInfoBar = ({
  video,
  playlistId,
  onNextVideo,
  onBackToAllPlaylists,
}: HomeHighlightVideoWithPlaylistInfoBarProps) => {
  const { data } = useQuery(getPlaylistQueryOption(playlistId));
  const videoHabitat = 'habitat' in video ? video.habitat : (video as Video).habitatId;

  const { data: habitat } = useQuery({
    ...getHabitatQueryOptions(videoHabitat?.slug, videoHabitat?.zoo.slug),
    select: (data) => ({
      _id: data.habitat._id,
      title: data.habitat.title,
      profileImage: data.habitat.profileImage,
      zoo: data.habitat.zoo,
      slug: data.habitat.slug,
    }),
  });

  return (
    <div className="relative z-10 flex items-center justify-between bg-white px-6 py-5 shadow-[0px_4px_12px_0px_rgba(0,0,0,0.07)] desktop:bg-white desktop:text-black">
      <div className="flex items-center gap-8 pr-2 mobile:hidden">
        {habitat && (
          <div className="preflight flex shrink-0 items-center gap-9 border-r border-[#D9D9D9] pr-3">
            <div className="pb-3">
              <HabitatProfileImageLink
                habitatId={habitat._id}
                slug={habitat.slug}
                zooSlug={habitat.zoo.slug}
                profileImage={habitat.profileImage}
                title={habitat.title}
                zooLogo={habitat.zoo.logo}
                zooName={habitat.zoo.name}
                entryPoint="highlight-info-bar"
              />
            </div>
            <div className="flex flex-col gap-1">
              <HabitatLink
                productsPromptModalState={{ entryPoint: 'highlight-info-bar' }}
                habitatId={habitat._id}
                slug={habitat.slug}
                zooSlug={habitat.zoo.slug}
                className="preflight preflight-a mb-1 text-xl font-medium text-grey-1 hover:text-grey-3"
                onClick={() => {
                  trackMP('visit-habitat-link-clicked', {
                    habitatId: habitat._id,
                    entryPoint: 'highlight-info-bar',
                  });
                }}
              >
                {habitat.title}
              </HabitatLink>
              <div className="w-[120px]">
                <FollowButton habitatId={habitat._id} habitatName={habitat.title} place="home-playlist-info-bar" />
              </div>
            </div>
          </div>
        )}
        <div className="preflight flex min-w-0 flex-col justify-center">
          <h4 className="preflight preflight-h4 text-lg">{data?.title}</h4>
          <h5 className="preflight preflight-h5 line-clamp-1 text-lg font-medium">{video.title}</h5>
        </div>
      </div>
      <a
        data-native
        className="preflight preflight-a inline-flex items-center gap-1 text-xs text-secondary-green desktop:hidden"
        href="?type=highlight"
        onClick={(e) => {
          e.preventDefault();
          onBackToAllPlaylists?.();
        }}
      >
        <FontAwesomeIcon icon={faArrowLeft} />
        All Highlights
      </a>
      <div className="flex shrink-0 gap-2">
        {videoHabitat && (
          <HabitatLink
            productsPromptModalState={{ entryPoint: 'highlight-info-bar' }}
            habitatId={videoHabitat._id}
            slug={videoHabitat.slug}
            zooSlug={videoHabitat.zoo.slug}
            className="preflight preflight-a flex items-center justify-center rounded-full border border-primary-green px-3 py-1 text-center text-xs font-medium text-primary-green hover:bg-beige-light desktop:hidden"
            onClick={() => {
              trackMP('visit-habitat-link-clicked', {
                habitatId: videoHabitat._id,
                entryPoint: 'highlight-info-bar',
              });
            }}
          >
            Visit Habitat
          </HabitatLink>
        )}
        {(data?.videos.length ?? 0) > 1 && (
          <button
            type="button"
            onClick={() => {
              onNextVideo?.();
              trackMP('next-episode-button-clicked', {
                entryPoint: 'highlight-info-bar',
              });
            }}
            className="preflight preflight-button rounded-full border border-transparent bg-primary-green px-3 py-1 text-xs font-medium text-white hover:bg-secondary-green desktop:px-4 desktop:py-2 desktop:text-sm desktop:font-medium"
          >
            Next Episode
          </button>
        )}
      </div>
    </div>
  );
};

export default HomeHighlightVideoWithPlaylistInfoBar;
